import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { ModalTypes } from '../../helpers/enums';
import validateCharLength from '../../helpers/validation/validateCharLength';
import ConfirmationType from './ConfirmationType';
import './CustomModal.css';
import IncompatibleBrowserType from './IncompatibleBrowserType';
import InputType from './InputType';

const defaultStyles = {
  content: {
    top: '40%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    padding: '19px 16px',
    textAlign: 'center',
    transform: 'translate(-50%, -50%)',
  },
  overlay: {
    backgroundColor: 'rgb(0,0,0,0.5)',
    zIndex: '5',
  },
};

const CustomModal = ({
  isOpen,
  type,
  handleOnClose,
  handleOnConfirm,
  modalTitle,
  includeButtons,
  closeButtonText,
  confirmButtonText,
  inputTitle,
  inputDefaultValue,
  maxCharLength,
  confirmationText,
  customStyles,
}) => {
  useEffect(() => {
    Modal.setAppElement('body');
  }, []);

  const [inputValue, setInputValue] = useState(inputDefaultValue);
  const [isValid, errorMessage] = validateCharLength(inputValue, maxCharLength);

  const handleClose = () => {
    setInputValue(inputDefaultValue);
    handleOnClose();
  };

  const renderBody = () => {
    switch (type) {
      case ModalTypes.INPUT:
        return (
          <InputType
            inputTitle={inputTitle}
            inputValue={inputValue}
            isValid={isValid}
            maxCharLength={maxCharLength}
            errorMessage={errorMessage}
            onInputChange={(e) => setInputValue(e.target.value)}
          />
        );
      case ModalTypes.CONFIRMATION:
        return <ConfirmationType confirmationText={confirmationText} />;
      case ModalTypes.INCOMPATIBLE_BROWSER:
        return <IncompatibleBrowserType />;
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} style={customStyles ?? defaultStyles}>
      <div className="modal-content">
        <div className="modal-title-container">
          <h3>{modalTitle}</h3>
          <button
            type="button"
            className="modal-title-close-button icon-close"
            onClick={handleClose}
            aria-label="Close Modal"
          />
        </div>
        <div className="modal-body-container">{renderBody()}</div>
        {includeButtons ? (
          <div className="modal-button-container">
            {closeButtonText && (
              <button type="button" className="modal-close-button" onClick={handleClose}>
                <div className="modal-button-text">{closeButtonText}</div>
              </button>
            )}
            {confirmButtonText && handleOnConfirm && (
              <button
                type="button"
                className={`modal-confirm-button ${isValid ? '' : 'inactive'}`}
                onClick={() => {
                  const trimmedValue = inputValue?.trim();
                  setInputValue(trimmedValue);
                  handleOnConfirm(trimmedValue);
                }}
                disabled={!isValid}
              >
                <div className="modal-button-text">{confirmButtonText}</div>
              </button>
            )}
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

CustomModal.defaultProps = {
  closeButtonText: null,
  confirmationText: null,
  confirmButtonText: null,
  handleOnConfirm: null,
  includeButtons: true,
  inputTitle: null,
  inputDefaultValue: null,
  maxCharLength: null,
  modalTitle: null,
  customStyles: null,
};

CustomModal.propTypes = {
  closeButtonText: PropTypes.string,
  confirmButtonText: PropTypes.string,
  handleOnConfirm: PropTypes.func,
  handleOnClose: PropTypes.func.isRequired,
  includeButtons: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  modalTitle: PropTypes.string,
  type: PropTypes.oneOf(Object.values(ModalTypes)).isRequired,
  // input-specific prop values
  inputTitle: PropTypes.string,
  inputDefaultValue: PropTypes.string,
  maxCharLength: PropTypes.number,
  // confirmation-specific prop values
  confirmationText: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  customStyles: PropTypes.shape({}),
};

export default CustomModal;
